import React, { useEffect } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Eye, 
  UserCheck, 
  Database, 
  Mail, 
  Activity, 
  CheckCircle2, 
  Clock 
} from 'lucide-react';
import Footer from '../../Components/Footer/Footer';
import Navbar from '../../Components/Navbar/Navbar';

const PrivacyPolicy = () => {

    useEffect(() => {
        document.title = 'นโยบายความเป็นส่วนตัว | ราชพฤกษ์ไตเทียม - PDPA Compliance';
        
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', 'นโยบายความเป็นส่วนตัวของศูนย์ฟอกเลือดด้วยเครื่องไตเทียม ราชพฤกษ์ เราปฏิบัติตามมาตรฐาน PDPA เพื่อคุ้มครองข้อมูลส่วนบุคคลของคุณอย่างเคร่งครัด');
        }
    }, []);

    const policies = [
        {
            title: "1. ข้อมูลที่เราจัดเก็บ",
            icon: Database,
            color: "text-blue-600",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
            content: "เราจัดเก็บข้อมูลที่จำเป็นสำหรับการให้บริการทางการแพทย์ เช่น ชื่อ-นามสกุล, ข้อมูลการติดต่อ, ประวัติการรักษาพยาบาล และข้อมูลการใช้บริการผ่านเว็บไซต์ เพื่อให้การดูแลรักษาเป็นไปอย่างมีประสิทธิภาพที่สุด"
        },
        {
            title: "2. วัตถุประสงค์การใช้งาน",
            icon: Eye,
            color: "text-orange-600",
            bgColor: "bg-orange-50",
            borderColor: "border-orange-200",
            content: "ข้อมูลของท่านจะถูกนำไปใช้เพื่อการนัดหมายการรักษา, การให้คำปรึกษาทางการแพทย์, การปรับปรุงคุณภาพบริการ และการส่งข้อมูลข่าวสารที่สำคัญเกี่ยวกับสุขภาพที่ท่านอาจสนใจ"
        },
        {
            title: "3. การคุ้มครองข้อมูล",
            icon: Lock,
            color: "text-emerald-600",
            bgColor: "bg-emerald-50",
            borderColor: "border-emerald-200",
            content: "เราใช้ระบบรักษาความปลอดภัยที่ได้รับมาตรฐานสากล ทั้งเทคโนโลยีการเข้ารหัสข้อมูล (Encryption) และมาตรการป้องกันการเข้าถึงข้อมูลโดยไม่ได้รับอนุญาต เพื่อรักษาความลับทางการแพทย์ของท่าน"
        },
        {
            title: "4. สิทธิของเจ้าของข้อมูล",
            icon: UserCheck,
            color: "text-indigo-600",
            bgColor: "bg-indigo-50",
            borderColor: "border-indigo-200",
            content: "ท่านมีสิทธิในการตรวจสอบ แก้ไข หรือขอยกเลิกการจัดเก็บข้อมูลส่วนบุคคลของท่านได้ตามกฎหมาย PDPA โดยสามารถติดต่อเจ้าหน้าที่คุ้มครองข้อมูลส่วนบุคคลของศูนย์ฯ ได้โดยตรง"
        },
        {
            title: "5. การเปิดเผยข้อมูล",
            icon: ShieldCheck,
            color: "text-rose-600",
            bgColor: "bg-rose-50",
            borderColor: "border-rose-200",
            content: "เราจะไม่เปิดเผยข้อมูลส่วนบุคคลของท่านแก่บุคคลภายนอก ยกเว้นกรณีที่ได้รับความยินยอมจากท่าน หรือเป็นการปฏิบัติตามคำสั่งของศาลและกฎหมายที่เกี่ยวข้องเท่านั้น"
        },
        {
            title: "6. การติดต่อเรา",
            icon: Mail,
            color: "text-slate-600",
            bgColor: "bg-slate-50",
            borderColor: "border-slate-200",
            content: "หากท่านมีข้อสงสัยเกี่ยวกับนโยบายความเป็นส่วนตัว หรือต้องการใช้สิทธิตามกฎหมายคุ้มครองข้อมูลส่วนบุคคล สามารถส่งคำร้องผ่านช่องทางอีเมลหรือเบอร์โทรศัพท์อย่างเป็นทางการของเรา"
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 font-sans text-slate-900 flex flex-col">
            
            <Navbar />

            {/* Main Content Area */}
            <main className="flex-grow pt-32 pb-24 md:pt-40 md:pb-32">
                
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    {/* Hero Header - Enhanced */}
                    <div className="text-center mb-16 md:mb-20">
                        <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-xs font-black tracking-[0.2em] text-blue-600 uppercase bg-blue-100 rounded-full border border-blue-200">
                            <Lock className="w-4 h-4" />
                            Legal Document
                        </div>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-slate-900 mb-6 tracking-tight leading-tight">
                            Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-orange-600">Policy</span>
                        </h1>
                        <div className="w-24 h-1.5 bg-gradient-to-r from-blue-600 to-orange-600 rounded-full mx-auto mb-8"></div>
                        <p className="text-slate-600 max-w-3xl mx-auto text-lg md:text-xl font-medium leading-relaxed">
                            ความเชื่อมั่นและความปลอดภัยของข้อมูลท่านคือสิ่งสำคัญที่สุด 
                            เราสัญญาว่าจะดูแลข้อมูลส่วนบุคคลตามมาตรฐาน <span className="font-bold text-blue-900">PDPA</span> อย่างเคร่งครัด
                        </p>
                    </div>

                    {/* Policy Card Wrapper - Enhanced */}
                    <div className="bg-white rounded-[3rem] shadow-2xl shadow-blue-900/10 p-8 md:p-16 lg:p-20 border-2 border-slate-100 relative overflow-hidden">
                        
                        {/* Decorative background shapes */}
                        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full -mr-48 -mt-48 opacity-30 blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-100 rounded-full -ml-48 -mb-48 opacity-30 blur-3xl"></div>

                        {/* Top Info Bar - Enhanced */}
                        <div className="flex flex-col sm:flex-row items-center justify-between mb-16 pb-10 border-b-2 border-slate-100 gap-6 relative z-10">
                            <div className="flex items-center gap-5 text-center sm:text-left">
                                <div className="p-5 bg-gradient-to-br from-blue-900 to-blue-800 rounded-[2rem] shadow-xl shadow-blue-900/30 group-hover:rotate-0 transition-transform">
                                    <Lock className="w-10 h-10 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-3xl font-black text-slate-900 tracking-tight">นโยบายความเป็นส่วนตัว</h2>
                                    <div className="flex items-center gap-2 mt-2 text-slate-500 font-bold text-xs">
                                        <Clock className="w-3.5 h-3.5" />
                                        <span>อัปเดตล่าสุด: 18 มกราคม 2569</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2.5 text-blue-600 bg-blue-50 px-5 py-3 rounded-2xl border-2 border-blue-200 shadow-sm">
                                <CheckCircle2 className="w-5 h-5" />
                                <span className="text-xs font-black uppercase tracking-wider">PDPA Compliance</span>
                            </div>
                        </div>

                        {/* Content Grid - Enhanced */}
                        <div className="grid grid-cols-1 gap-12 relative z-10">
                            {policies.map((policy, index) => (
                                <div key={index} className={`group flex flex-col md:flex-row gap-6 items-start p-6 -m-6 rounded-3xl hover:bg-gradient-to-br hover:from-slate-50 hover:to-blue-50 transition-all duration-500 border-2 border-transparent hover:${policy.borderColor}`}>
                                    <div className="md:w-1/3 flex items-start gap-4">
                                        <div className={`shrink-0 p-4 ${policy.bgColor} shadow-lg border-2 ${policy.borderColor} ${policy.color} rounded-2xl group-hover:scale-110 group-hover:-translate-y-1 transition-all duration-500`}>
                                            <policy.icon className="w-7 h-7" strokeWidth={2} />
                                        </div>
                                        <h3 className="font-black text-xl md:text-2xl text-slate-900 leading-tight pt-2">{policy.title}</h3>
                                    </div>
                                    <div className="md:w-2/3">
                                        <p className="text-slate-600 leading-relaxed text-base md:text-lg font-medium group-hover:text-slate-900 transition-colors">
                                            {policy.content}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>

                    {/* Bottom CTA Section - Enhanced */}
                    <div className="mt-16 p-10 md:p-14 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 rounded-[3rem] text-white relative overflow-hidden shadow-2xl shadow-blue-900/30">
                        <div className="absolute inset-0 opacity-20">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full -ml-32 -mb-32 blur-3xl"></div>
                        </div>
                        <div className="relative z-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 border border-white/20">
                                <Activity className="w-8 h-8 text-orange-400" />
                            </div>
                            <h3 className="text-3xl md:text-4xl font-black mb-4 tracking-tight">ข้อมูลของคุณปลอดภัยกับเรา</h3>
                            <p className="text-blue-100 mb-10 max-w-2xl leading-relaxed text-lg">
                                หากท่านต้องการใช้สิทธิในการเข้าถึงข้อมูล หรือมีคำถามเกี่ยวกับความปลอดภัยของระบบ 
                                สามารถติดต่อเจ้าหน้าที่คุ้มครองข้อมูลส่วนบุคคล (DPO) ของเราได้ทันที
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <a 
                                    href="tel:0811506992"
                                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-10 py-4 rounded-2xl font-black transition-all shadow-xl shadow-orange-500/30 active:scale-95"
                                >
                                    ติดต่อศูนย์ช่วยเหลือ
                                </a>
                                <button className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/20 px-10 py-4 rounded-2xl font-black transition-all backdrop-blur-sm">
                                    ดาวน์โหลดเอกสาร PDF
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </main>

            <Footer />

            {/* SEO Content (Hidden but crawlable) */}
            <div className="sr-only">
                <h2>นโยบายความเป็นส่วนตัว ราชพฤกษ์ไตเทียม</h2>
                <p>
                    นโยบายคุ้มครองข้อมูลส่วนบุคคล PDPA ของศูนย์ฟอกเลือดด้วยเครื่องไตเทียม ราชพฤกษ์ 
                    เราให้ความสำคัญกับความเป็นส่วนตัวและความปลอดภัยของข้อมูลผู้ป่วยอย่างเคร่งครัด
                </p>
            </div>

        </div>
    );
};

export default PrivacyPolicy;
